import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { PaginatedResponse } from '@/features/companies/api/companies-api'
import type { Invoice, SortDirection } from '@/features/invoices/types'
import type { SubscriptionBillingType, SubscriptionStatus } from '@vanta-web/types'

import { api } from '@/lib/api-client'

const ACCOUNT_KEY = ['account'] as const

// Espelha a superfície `account` da API (client-surface.ts): o pagador é SEMPRE o cliente da
// sessão, resolvido pelo `CustomerScopeGuard`. Nenhum id do cliente trafega no request.
export interface AccountCustomer {
  id: string
  name: string
  email: string
  document: string | null
  planId: string | null
  planName: string | null
  effectivePriceCents: number | null
  billingExempt: boolean
  isActive: boolean
  trialEndsAt: string | null
  subscriptionStatus: SubscriptionStatus | null
  // Metodo em uso: decide o que o portal oferece (trocar cartao, migrar para Pix, etc).
  subscriptionBillingType: SubscriptionBillingType | null
  hasActiveSubscription: boolean
  // Fim do período já pago (ISO). `cancelled` com esta data no futuro = acesso mantido até lá.
  subscriptionCurrentPeriodEndsAt: string | null
}

interface AccountSessionResponse {
  valid: boolean
  allowed: boolean
  reason: string
  customer?: AccountCustomer | null
}

export function useAccountSession() {
  return useQuery({
    queryKey: [...ACCOUNT_KEY, 'session'],
    queryFn: async () => {
      const { data } = await api.get<AccountSessionResponse>('/api/customer-account/session')
      return data
    },
  })
}

// Ordenação da lista de faturas do cliente. Só os campos que a API do cliente aceita
// (customer-invoices.controller): sem `amountCents`/`issueDate` (não são allow-listed lá).
export const ACCOUNT_INVOICE_SORT_FIELDS = ['dueDate', 'referenceMonth'] as const
export type AccountInvoiceSortField = (typeof ACCOUNT_INVOICE_SORT_FIELDS)[number]

export const DEFAULT_ACCOUNT_INVOICE_SORT_FIELD: AccountInvoiceSortField = 'dueDate'

export const ACCOUNT_INVOICE_STATUS_FILTERS = ['all', 'PENDING', 'PAID', 'CANCELED'] as const
export type AccountInvoiceStatusFilter = (typeof ACCOUNT_INVOICE_STATUS_FILTERS)[number]

export const ACCOUNT_INVOICES_PER_PAGE = 20

export interface AccountInvoicesParams {
  page: number
  perPage: number
  sortBy: AccountInvoiceSortField
  sortDir: SortDirection
  status: AccountInvoiceStatusFilter
}

export function useAccountInvoices(params: AccountInvoicesParams) {
  const { page, perPage, sortBy, sortDir, status } = params

  return useQuery({
    queryKey: [...ACCOUNT_KEY, 'invoices', params],
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<Invoice>>('/api/customer-account/invoices', {
        params: {
          page,
          perPage,
          sortBy,
          sortDir,
          status: status === 'all' ? undefined : status,
        },
      })
      return data
    },
    placeholderData: keepPreviousData,
  })
}

// A resposta muda por método escolhido, e é isso que decide a tela: cartão manda o cliente para
// o checkout do gateway (é lá que ele digita o cartão, nunca aqui), Pix devolve o QR a pagar.
// União discriminada para não existir o estado impossível "cartão com QR".
export type SubscribeResult =
  | { billingType: 'CREDIT_CARD'; checkoutUrl: string }
  | { billingType: 'PIX'; subscriptionId: string; pixPayload: string; pixEncodedImage: string }

export interface SubscribeInput {
  billingType: SubscriptionBillingType
  // Troca de cartao / Pix->cartao: emite checkout novo para quem ja assina. A assinatura
  // anterior so e encerrada quando o novo for pago.
  replaceExisting?: boolean
  // Nome do pagador. O gateway recusa nome de uma palavra só, e é aqui que ele é corrigido.
  name: string
  document: string
  phone: string
  postalCode: string
  address: string
  addressNumber: string
  province: string
  city: string
}

// Cria a assinatura no gateway. O status ATIVA só chega pelo webhook; a sessão é invalidada
// para refletir a mudança assim que ela chegar.
export function useSubscribe() {
  const qc = useQueryClient()

  return useMutation({
    meta: { suppressGlobalError: true },
    mutationFn: async (input: SubscribeInput) => {
      const { data } = await api.post<SubscribeResult>(
        '/api/customer-account/subscription',
        input,
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...ACCOUNT_KEY, 'session'] }),
  })
}

// Cartao -> Pix na MESMA assinatura: sem checkout, sem captura de cartao. Vale do proximo
// ciclo em diante; o periodo ja pago nao muda.
export function useSwitchToPix() {
  const qc = useQueryClient()

  return useMutation({
    meta: { suppressGlobalError: true },
    mutationFn: async () => {
      const { data } = await api.post<{ status: 'switched' | 'already_pix' }>(
        '/api/customer-account/subscription/switch-to-pix',
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...ACCOUNT_KEY, 'session'] }),
  })
}

// O cancelamento vale até o fim do período já pago: a API devolve `accessEndsAt` (ISO ou null),
// que a UI mostra num toast ("acesso até {data}"). A sessão é invalidada para refletir o status.
export interface CancelSubscriptionResult {
  status: 'cancelled' | 'no_subscription'
  accessEndsAt: string | null
}

export function useCancelSubscription() {
  const qc = useQueryClient()

  return useMutation({
    meta: { suppressGlobalError: true },
    mutationFn: async () => {
      const { data } = await api.delete<CancelSubscriptionResult>(
        '/api/customer-account/subscription',
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...ACCOUNT_KEY, 'session'] }),
  })
}
